import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTestCaseQuery, useProductQuery, useModuleQuery } from "../useQuery";
import { useSelector } from "react-redux";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InboxIcon from "@mui/icons-material/Inbox";
import StudioSelection from "./Studio/StudioSelection";

const HeaderCell = styled(TableCell)(() => ({
  borderBottom: "1px solid rgba(0,0,0,0.12)",
  fontSize: 11,
  fontWeight: 700,
  color: "rgba(0,0,0,0.54)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  padding: "10px 14px",
  backgroundColor: "#f9fafb",
}));

const BodyCell = styled(TableCell)(() => ({
  padding: "12px 14px",
}));

const TABLE_COLUMNS = ["Test Case #", "Test Case Name", "Module", "Status"];

const TestCaseListPage = () => {
  const navigate = useNavigate();

  const selectedApp = useSelector((state) => state.authoringSlice.selectedProduct);
  const selectedModule = useSelector((state) => state.authoringSlice.selectedComponent);

  const [checkedIds, setCheckedIds] = React.useState(() => new Set());
  const [testCaseSearchKeyword, setTestCaseSearchKeyword] = React.useState("");
  const [debouncedSearchKeyword, setDebouncedSearchKeyword] = React.useState("");

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchKeyword(testCaseSearchKeyword), 500);
    return () => clearTimeout(handler);
  }, [testCaseSearchKeyword]);

  const { data: products = [] } = useProductQuery();
  const { data: modules = [] } = useModuleQuery(selectedApp, { enabled: !!selectedApp });
  const { data: testCases = [], isLoading } = useTestCaseQuery(
    selectedModule,
    debouncedSearchKeyword,
    { enabled: !!selectedModule },
  );

  const allChecked =
    testCases.length > 0 && testCases.every((tc) => checkedIds.has(tc.idtest_case));

  const toggleAll = () => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (allChecked) testCases.forEach((tc) => next.delete(tc.idtest_case));
      else testCases.forEach((tc) => next.add(tc.idtest_case));
      return next;
    });
  };

  const toggleOne = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleRowClick = (tc) => {
    navigate("/new/test-studio", {
      state: {
        idtest_case: tc.idtest_case,
        Test_Case: tc.Test_Case,
        idproducts: selectedApp,
        idmodules: selectedModule,
        fromExecutionReports: false,
        fromTestCaseList: true,
      },
    });
  };

  return (
    <Box sx={{ backgroundColor: "#f5f7f9", minHeight: "100vh" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 3.5,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "grey.200",
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ cursor: "pointer", "&:hover": { color: "text.primary" } }}
            onClick={() => navigate("/new/dashboard")}
          >
            Home
          </Typography>
          <ChevronRightIcon sx={{ fontSize: 14, color: "grey.400" }} />
          <Typography variant="body2" fontWeight={500} color="text.primary">
            Test Case List
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 3.5, py: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
          <Typography variant="h6" fontWeight={600} color="text.primary" lineHeight={1}>
            Test Case List
          </Typography>
          <Chip
            label={`${testCases.length} TC`}
            size="small"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              backgroundColor: "#e0e7ff",
              color: "#3730a3",
              height: 20,
            }}
          />
          {isLoading && <CircularProgress size={14} thickness={5} sx={{ color: "#6366f1" }} />}
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <StudioSelection
            products={products}
            modules={modules}
            testCases={testCases}
            isLoadingTestcase={isLoading && !!selectedModule}
            testCaseSearchKeyword={testCaseSearchKeyword}
            setTestCaseSearchKeyword={setTestCaseSearchKeyword}
            setRefreshFlag={() => {}}
            setHasUserClearedFilters={() => {}}
          />
        </Box>
        {!selectedModule || (!isLoading && testCases.length === 0) ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 12,
              gap: 1.5,
            }}
          >
            <InboxIcon sx={{ fontSize: 48, color: "grey.300" }} />
            <Typography variant="body1" fontWeight={600} color="text.secondary">
              {!selectedApp
                ? "Select filters to view test cases"
                : !selectedModule
                  ? "Select a module to view test cases"
                  : "No test cases found for this module"}
            </Typography>
            <Typography
              variant="body2"
              color="text.disabled"
              textAlign="center"
              maxWidth={360}
              lineHeight={1.6}
            >
              {!selectedApp || !selectedModule
                ? "Please select an Application and Module from the filters above to display the test case list."
                : "This module has no test cases yet."}
            </Typography>
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, overflow: "hidden", borderColor: "grey.200" }}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <HeaderCell padding="checkbox" sx={{ width: 40 }}>
                      <Checkbox
                        size="small"
                        checked={allChecked}
                        onChange={toggleAll}
                        sx={{ p: 0 }}
                      />
                    </HeaderCell>
                    {TABLE_COLUMNS.map((col) => (
                      <HeaderCell
                        key={col}
                        sx={col === "Test Case Name" ? { width: 400, maxWidth: 400 } : {}}
                      >
                        {col}
                      </HeaderCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {testCases.map((tc) => {
                    const status = tc.laststatus ?? "—";
                    const checked = checkedIds.has(tc.idtest_case);
                    const isPass = status === "PASS";

                    return (
                      <TableRow
                        key={tc.idtest_case}
                        sx={{
                          borderBottom: "1px solid",
                          borderColor: "grey.100",
                          backgroundColor: checked ? "#f0f7ff" : "transparent",
                          transition: "background-color 0.1s",
                          "&:hover": { backgroundColor: checked ? "#f0f7ff" : "grey.50" },
                        }}
                      >
                        <BodyCell padding="checkbox" sx={{ width: 40 }}>
                          <Checkbox
                            size="small"
                            checked={checked}
                            onChange={() => toggleOne(tc.idtest_case)}
                            sx={{ p: 0 }}
                          />
                        </BodyCell>

                        <BodyCell>
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {tc.idtest_case}
                          </Typography>
                        </BodyCell>

                        <BodyCell
                          sx={{ cursor: "pointer", maxWidth: 400, width: 400 }}
                          onClick={() => handleRowClick(tc)}
                        >
                          <Tooltip title={tc.Test_Case} placement="top" arrow>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              color="text.primary"
                              noWrap
                            >
                              {tc.Test_Case}
                            </Typography>
                          </Tooltip>
                        </BodyCell>

                        <BodyCell>
                          <Typography variant="body2" color="text.primary">
                            {modules.find((m) => m.idmodules === tc.Module)?.modulename ?? "—"}
                          </Typography>
                        </BodyCell>

                        <BodyCell>
                          <Chip
                            label={status}
                            size="small"
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              height: 20,
                              backgroundColor: isPass ? "#dcfce7" : "#fee2e2",
                              color: isPass ? "#166534" : "#991b1b",
                            }}
                          />
                        </BodyCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid", borderColor: "grey.200" }}>
              <Typography variant="body2" color="text.secondary">
                {testCases.length} test case{testCases.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default TestCaseListPage;
